import { z } from 'zod';
import { Navigate, useParams } from 'react-router-dom';

import { buildExplorationFromPipelines } from '@/core/exploration';
import { extractSubpipelines } from '@/core/pipeline';

import { useBuildAccountUrl, useSelectedAccount } from '../lib/accounts/context';
import { FullPageLoader } from '../components/loader';
import { useAlertConfigurationQuery } from '../graphql';
import { convertAlertConfigurationTypes } from '../explore/input';
import { buildExplorationHashUrl, getHashParameter } from '../explore/utils';
import { isNil } from '../lib/utils';
import { ErrorBanner } from '../components/banner';

const alertFiltersZod = z.array(
  z.object({
    key: z.string(),
    value: z.string(),
  }),
);

export const RedirectToAlertExploration = () => {
  const account = useSelectedAccount();
  const buildAccountUrl = useBuildAccountUrl();
  const { alertConfigurationId } = useParams();

  const filtersJson = getHashParameter(location.hash, 'filters');

  const { data: alertConfigurationData, loading } = useAlertConfigurationQuery({
    variables: { accountId: account.accountId, alertConfigurationId: alertConfigurationId ?? '' },
    skip: alertConfigurationId === undefined,
  });

  if (alertConfigurationId === undefined) {
    return <ErrorBanner title="Missing ID" description="The link may be invalid or incomplete" />;
  }

  if (loading) {
    return <FullPageLoader />;
  }

  const alertConfiguration = convertAlertConfigurationTypes(
    alertConfigurationData?.account?.alertConfiguration,
  );

  const models = (alertConfigurationData?.account?.models ?? []).map((model) => ({
    modelId: model.modelId,
    name: model.name,
    primaryKey: [],
    relations: [],
    properties: [],
    labels: {},
  }));

  if (isNil(alertConfiguration)) {
    return <ErrorBanner title="Not Found" description="Alert configuration not found" />;
  }

  let filters;
  try {
    filters = alertFiltersZod.parse(JSON.parse(filtersJson ?? '[]'));
  } catch {
    return (
      <ErrorBanner title="Invalid parameters" description="The link may be invalid or incomplete" />
    );
  }
  const { pipeline, subPipelines } = extractSubpipelines(alertConfiguration.pipeline);

  // Apply filters to alert pipeline
  const filteredPipeline = {
    ...pipeline,
    operations: [
      ...pipeline.operations,
      ...filters.map(({ key, value }) => ({
        operation: 'filter' as const,
        parameters: {
          key,
          operator: '==' as const,
          value,
        },
      })),
    ],
  };

  const explorationUrl = buildAccountUrl(
    buildExplorationHashUrl(
      buildExplorationFromPipelines(
        alertConfiguration.name,
        [filteredPipeline, ...subPipelines],
        models,
        { collapsePipelines: true },
      ),
    ),
  );

  return <Navigate to={explorationUrl} replace />;
};
